/**
 * Convierte la respuesta de Regula Document Reader a una estructura compatible
 * con BlinkID (BlinkIdSingleSideRecognizer / BlinkIdMultiSideRecognizer) para que
 * binder.setOcrResult() y el backend sigan funcionando sin cambios.
 */

// Regula Text field types (TextFieldType) -> Blink result key
// Ref: Regula Document Reader SDK (0-2 doc, 3-5 dates, 6 place of birth, 7 personal number, 8-9 name, 11-17 personal, 20-22 DL, 36 optional, 145 apellido materno)
const REGULA_TEXT_FIELD_TO_BLINK = {
  0: "documentClass",
  1: "issuingState",
  2: "documentNumber",
  3: "dateOfExpiry",
  4: "dateOfIssue",
  5: "dateOfBirth",
  6: "placeOfBirth",
  7: "personalIdNumber",
  8: "lastName",
  9: "firstName",
  11: "nationality",
  12: "sex",
  13: "height",
  14: "weight",
  15: "eyeColor",
  16: "hairColor",
  17: "address",
  20: "dlClass",
  21: "dlEndorsement",
  22: "dlRestriction",
  36: "optionalData",
  51: "mrzString", // MRZ
  145: "secondSurname", // apellido materno
  70: "dateOfRegistration", // año de registro
  84: "issueNumber", // número de emisión
  187: "voterKey", // clave del lector
};

// Regula Graphic field types (eGraphicFieldType)
const REGULA_GRAPHIC = {
  PORTRAIT: 201,
  DOCUMENT_FRONT: 207,
  DOCUMENT_REAR: 208,
  SIGNATURE: 204,
};

/** Nombres para log de imágenes (tipo gráfico -> etiqueta). */
const GRAPHIC_FIELD_NAMES = {
  201: "Retrato",
  207: "Frente",
  208: "Dorso",
  204: "Firma",
};

const RESULT_TEXT = 36;
const RESULT_IMAGES = 37;

/** Llaves del objeto result en formato Blink (mismas que espera el backend/binder). */
// const BLINK_RESULT_KEYS = new Set(Object.values(REGULA_TEXT_FIELD_TO_BLINK));
/** Campos que son fechas y deben tener formato { day, month, year, originalString, successfullyParsed }. */
const BLINK_DATE_KEYS = new Set([
  "dateOfBirth",
  "dateOfIssue",
  "dateOfExpiry",
  "dateOfRegistration",
]);
const BLINK_IMAGE_KEYS = [
  "fullDocumentImage",
  "fullDocumentFrontImage",
  "fullDocumentBackImage",
  "faceImage",
  "signatureImage",
];

/** Formato Blink para un campo de texto: objeto con latin, arabic, cyrillic, greek, latinLocation. */
function toBlinkTextValue(str) {
  const s = str != null && str !== "" ? String(str).trim() : "";
  return {
    latin: s,
    arabic: "",
    cyrillic: "",
    greek: "",
    latinLocation: undefined,
  };
}

/** Parsea string de fecha común (DD.MM.YYYY, YYYY-MM-DD, DDMMYY, etc.) a day, month, year. */
function parseDateString(str) {
  if (!str || typeof str !== "string") return null;
  const s = str.trim();
  if (!s) return null;
  // DD.MM.YYYY o DD/MM/YYYY
  let m = s.match(/^(\d{1,2})[./](\d{1,2})[./](\d{2,4})$/);
  if (m) {
    let day = parseInt(m[1], 10);
    let month = parseInt(m[2], 10);
    let year = parseInt(m[3], 10);
    if (year < 100) year += year < 50 ? 2000 : 1900;
    return { day, month, year };
  }
  // YYYY-MM-DD
  m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) {
    const year = parseInt(m[1], 10);
    const month = parseInt(m[2], 10);
    const day = parseInt(m[3], 10);
    return { day, month, year };
  }
  // DDMMYY o YYMMDD (6 dígitos)
  m = s.match(/^(\d{6})$/);
  if (m) {
    const n = m[1];
    const d1 = parseInt(n.slice(0, 2), 10);
    const d2 = parseInt(n.slice(2, 4), 10);
    const d3 = parseInt(n.slice(4, 6), 10);
    if (d1 >= 1 && d1 <= 31 && d2 >= 1 && d2 <= 12) {
      const year = d3 < 50 ? 2000 + d3 : 1900 + d3;
      return { day: d1, month: d2, year };
    }
    if (d3 >= 1 && d3 <= 31 && d2 >= 1 && d2 <= 12) {
      const year = d1 < 50 ? 2000 + d1 : 1900 + d1;
      return { day: d3, month: d2, year };
    }
  }
  // Solo año (4 dígitos o 2 dígitos) — p. ej. año de registro
  m = s.match(/^(\d{4})$/);
  if (m) return { day: 0, month: 0, year: parseInt(m[1], 10) };
  m = s.match(/^(\d{2})$/);
  if (m) {
    const y = parseInt(m[1], 10);
    return { day: 0, month: 0, year: y < 50 ? 2000 + y : 1900 + y };
  }
  return null;
}

/** Formato Blink para un campo de fecha: { day, month, year, originalString, successfullyParsed }. */
function toBlinkDateValue(str) {
  const originalString = str != null && str !== "" ? String(str).trim() : "";
  const parsed = parseDateString(originalString);
  if (parsed) {
    return {
      day: parsed.day,
      month: parsed.month,
      year: parsed.year,
      originalString: originalString || {},
      successfullyParsed: true,
    };
  }
  return {
    day: 0,
    month: 0,
    year: 0,
    originalString: originalString || {},
    successfullyParsed: false,
  };
}

/** Código postal: solo los últimos 5 dígitos (Regula a veces devuelve 7). */
function normalizePostalCodeToFiveDigits(str) {
  if (!str || typeof str !== "string") return str;
  const s = str.trim();
  if (s.length === 0) return s;
  const sevenDigits = s.match(/\d{7}$/) || s.match(/^\d{7}$/);
  if (sevenDigits) {
    const seven = sevenDigits[0];
    const five = seven.slice(-5);
    return s.length === 7 ? five : s.slice(0, -7) + five;
  }
  const atEnd = s.match(/(\D)(\d{7})$/);
  if (atEnd) return s.slice(0, -7) + atEnd[2].slice(-5);
  return s;
}

/** Convierte un valor string extraído de Regula al formato que espera el backend para result[key]. */
function toBlinkResultFieldValue(blinkKey, value) {
  if (value === undefined || value === null) return undefined;
  let str = String(value).trim();
  if (str === "") return undefined;
  if (blinkKey === "address") str = normalizePostalCodeToFiveDigits(str);
  if (BLINK_DATE_KEYS.has(blinkKey)) return toBlinkDateValue(str);
  return toBlinkTextValue(str);
}

/** Objeto vacío de texto Blink (latin, arabic, cyrillic, greek, latinLocation). */
function emptyBlinkText() {
  return { latin: "", arabic: "", cyrillic: "", greek: "", latinLocation: undefined };
}

/** Objeto vacío de fecha Blink. */
function emptyBlinkDate() {
  return {
    day: 0,
    month: 0,
    year: 0,
    originalString: {},
    successfullyParsed: false,
  };
}

/** Estructura completa de result como en BlinkID (imagen de ejemplo). Todos los campos con su forma por defecto. */
function getDefaultBlinkResult() {
  const text = emptyBlinkText;
  const date = emptyBlinkDate;
  return {
    firstName: text(),
    lastName: text(),
    fathersName: text(),
    mothersName: text(),
    fullName: text(),
    sex: text(),
    dateOfBirth: date(),
    nationality: text(),
    address: text(),
    placeOfBirth: text(),
    bloodType: text(),
    maritalStatus: text(),
    profession: text(),
    race: text(),
    religion: text(),
    employer: text(),
    additionalAddressInformation: text(),
    additionalNameInformation: text(),
    additionalOptionalAddressInformation: text(),
    documentNumber: text(),
    documentAdditionalNumber: text(),
    documentOptionalAdditionalNumber: text(),
    documentSubtype: text(),
    dateOfIssue: date(),
    dateOfExpiry: date(),
    dateOfExpiryPermanent: false,
    issuingAuthority: text(),
    classInfo: {
      country: 0,
      region: 0,
      documentType: 0,
      countryName: "",
      isoNumericCountryCode: "",
    },
    recognitionMode: 4,
    scanningFirstSideDone: true,
    state: 2,
    manufacturingYear: text(),
    mrz: {
      alienNumber: {},
      applicationReceiptNumber: {},
      dateOfBirth: {},
      dateOfExpiry: {},
      documentCode: {},
    },
    backAdditionalProcessingInfo: {
      missingMandatoryFields: [],
      invalidCharacterFields: [],
      extraPresentFields: [],
      imageExtractionFailures: [],
    },
    frontAdditionalProcessingInfo: {
      missingMandatoryFields: [],
      invalidCharacterFields: [],
      extraPresentFields: [],
      imageExtractionFailures: [],
    },
    backImageAnalysisResult: {
      blurDetected: false,
      glareDetected: false,
      cardOrientation: 0,
      documentImageColorStatus: 2,
      documentImageMoireStatus: 0,
    },
    frontImageAnalysisResult: {
      blurDetected: false,
      glareDetected: false,
      cardOrientation: 0,
      documentImageColorStatus: 2,
      documentImageMoireStatus: 0,
    },
    backCameraFrame: { frame: null, orientation: 0 },
    frontCameraFrame: { frame: null, orientation: 0 },
    backProcessingStatus: 0,
    frontProcessingStatus: 0,
    barcode: {
      additionalNameInformation: {},
      address: {},
      addressDetailedInfo: {},
      barcodeData: {},
      dateOfBirth: {},
    },
    barcodeCameraFrame: { frame: null, orientation: 0 },
    barcodeStepUsed: false,
    dataMatchResult: { state: 0, states: [] },
    dependentsInfo: [],
    driverLicenseDetailedInfo: {
      conditions: [],
      endorsements: [],
      restrictions: [],
      vehicleClass: [],
      vehicleClassesInfo: [],
    },
    eligibilityCategory: text(),
    localizedName: text(),
    personalIdNumber: text(),
    residencePermitType: text(),
    residentialStatus: text(),
    specificDocumentValidity: text(),
    sponsor: text(),
    vehicleOwner: text(),
    vehicleType: text(),
    visaType: text(),
    documentClass: text(),
    issuingState: text(),
    issuingStateName: text(),
    nationalityCode: text(),
    section: text(),
    voterKey: text(),
    bookletNumber: text(),
    issueNumber: text(), // número de emisión
    dateOfRegistration: date(),
    url: text(),
    secondSurname: text(),
    mrzString: text(),
    dlClass: text(),
    dlEndorsement: text(),
    dlRestriction: text(),
    optionalData: text(),
    height: text(),
    weight: text(),
    eyeColor: text(),
    hairColor: text(),
    faceImage: { rawImage: null, encodedImage: [] },
    fullDocumentBackImage: { rawImage: null, encodedImage: [] },
    fullDocumentFrontImage: { rawImage: null, encodedImage: [] },
    signatureImage: { rawImage: null, encodedImage: [] },
  };
}

/** Rellena el result por defecto con los datos extraídos (solo claves que existen en default y con formato correcto). */
function mergeExtractedIntoBlinkResult(defaultResult, extracted) {
  const result = { ...defaultResult };
  for (const [blinkKey, value] of Object.entries(extracted)) {
    if (!(blinkKey in result)) continue;
    const formatted = toBlinkResultFieldValue(blinkKey, value);
    if (formatted !== undefined) result[blinkKey] = formatted;
  }
  return result;
}

/** Dado un objeto plano (ej. backend result con strings), devuelve result con estructura Blink; respeta objetos ya con formato. */
function normalizeFlatResultToBlink(src) {
  const defaultResult = getDefaultBlinkResult();
  if (!src || typeof src !== "object") return defaultResult;
  const result = { ...defaultResult };
  for (const key of Object.keys(src)) {
    if (!(key in result)) continue;
    const raw = src[key];
    if (raw == null || raw === "") continue;
    if (
      typeof raw === "object" &&
      !Array.isArray(raw) &&
      (("latin" in raw) || ("day" in raw && "month" in raw))
    ) {
      result[key] = raw;
    } else {
      const blinkValue = toBlinkResultFieldValue(key, raw);
      if (blinkValue !== undefined) result[key] = blinkValue;
    }
  }
  return result;
}

/**
 * Obtiene el string de valor para un campo del result (para que el binder rellene campos).
 * - Objeto con .latin -> latin
 * - Objeto con .day/.month/.year -> "DD.MM.YYYY"
 * - String -> tal cual
 */
function getResultFieldDisplayString(value) {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object" && !Array.isArray(value)) {
    if ("latin" in value && value.latin != null && value.latin !== "") return String(value.latin).trim();
    if ("day" in value && "month" in value && "year" in value) {
      const d = value.day ?? 0;
      const m = value.month ?? 0;
      const y = value.year ?? 0;
      if (d || m || y) return [d, m, y].map((n) => String(n).padStart(2, "0")).join(".");
      if (value.originalString && typeof value.originalString === "string") return value.originalString.trim();
    }
  }
  return "";
}

/**
 * Añade al objeto blinkLike (recognizer) las propiedades planas en string que el binder
 * suele leer para rellenar los campos del formulario (recognizer.firstName, recognizer.documentNumber, etc.).
 * No modifica blinkLike.result; solo añade en la raíz del objeto.
 */
export function ensureBlinkLikeFlatStringsForBinder(blinkLike) {
  if (!blinkLike || !blinkLike.result || typeof blinkLike.result !== "object") return;
  const result = blinkLike.result;
  const keys = [
    "firstName",
    "lastName",
    "documentNumber",
    "documentClass",
    "issuingState",
    "dateOfBirth",
    "dateOfIssue",
    "dateOfExpiry",
    "nationality",
    "sex",
    "address",
    "height",
    "weight",
    "eyeColor",
    "hairColor",
    "dlClass",
    "dlEndorsement",
    "dlRestriction",
    "optionalData",
    "issueNumber",
    "voterKey",
    "bookletNumber",
  ];
  for (const key of keys) {
    blinkLike[key] = getResultFieldDisplayString(result[key]);
  }
}

/**
 * Convierte una cadena base64 (con o sin prefijo data:image/...;base64,) a ImageData.
 * El binder espera fullDocumentFrontImage.rawImage como ImageData para convertirlo a File con Jr().
 * @param {string} base64
 * @returns {Promise<ImageData|null>}
 */
export function base64ToImageData(base64) {
  if (!base64 || typeof base64 !== "string") return Promise.resolve(null);
  const trimmed = base64.trim();
  if (!trimmed) return Promise.resolve(null);
  const dataUrl = trimmed.startsWith("data:") ? trimmed : `data:image/jpeg;base64,${trimmed}`;
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(ctx.getImageData(0, 0, canvas.width, canvas.height));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

/**
 * Convierte blinkLike al formato que espera flowy-3-core binder.setOcrResult():
 * - Todas las propiedades de result en la raíz; fullName, fathersName, mothersName.
 * - Imágenes como { rawImage: ImageData } para que el binder use Jr() y setFile().
 * Si las imágenes vienen en base64, conviértelas antes con blinkLikeToBinderPayloadAsync.
 */
export function blinkLikeToBinderPayload(blinkLike) {
  return blinkLikeToBinderPayloadSync(blinkLike, null);
}

/**
 * Versión síncrona interna: si imageDataMap es null, las imágenes base64 se dejan como { rawImage: null }.
 * Si se pasa imageDataMap como { front, back, face }, se usan esos ImageData.
 */
function strFromField(field) {
  if (field == null) return "";
  if (typeof field === "string") return field.trim();
  if (typeof field === "object" && field.latin != null) return String(field.latin).trim();
  return "";
}

function textFromStr(s) {
  const t = (s ?? "").toString().trim();
  return { latin: t, arabic: "", cyrillic: "", greek: "", latinLocation: undefined };
}

function blinkLikeToBinderPayloadSync(blinkLike, imageDataMap) {
  if (!blinkLike) return {};
  const result = blinkLike.result && typeof blinkLike.result === "object" ? blinkLike.result : {};
  const emptyText = () => ({ latin: "", arabic: "", cyrillic: "", greek: "", latinLocation: undefined });

  const docNum = strFromField(result.documentNumber);
  const optData = strFromField(result.optionalData);
  const voterKeyVal = strFromField(result.voterKey);
  const secondSurnameVal = strFromField(result.secondSurname);
  const lastNameVal = strFromField(result.lastName);
  const dateOfReg = result.dateOfRegistration;
  const regYear =
    dateOfReg && typeof dateOfReg === "object" && dateOfReg.year
      ? String(dateOfReg.year)
      : dateOfReg && typeof dateOfReg === "object" && dateOfReg.originalString
        ? String(dateOfReg.originalString).slice(0, 4)
        : "";

  const payload = {
    fullName: result.firstName && typeof result.firstName === "object" ? result.firstName : emptyText(),
    fathersName: textFromStr(lastNameVal || strFromField(result.fathersName)),
    mothersName: textFromStr(secondSurnameVal || strFromField(result.mothersName)),
    firstName: result.firstName,
    lastName: result.lastName,
    documentNumber: result.documentNumber ?? emptyText(),
    documentAdditionalNumber: textFromStr(voterKeyVal || strFromField(result.documentAdditionalNumber)),
    documentOptionalAdditionalNumber: textFromStr(regYear || strFromField(result.documentOptionalAdditionalNumber)),
    documentClass: result.documentClass ?? emptyText(),
    issuingState: result.issuingState ?? emptyText(),
    dateOfBirth: result.dateOfBirth && typeof result.dateOfBirth === "object" ? result.dateOfBirth : { day: 0, month: 0, year: 0, originalString: {}, successfullyParsed: false },
    dateOfIssue: result.dateOfIssue && typeof result.dateOfIssue === "object" ? result.dateOfIssue : { day: 0, month: 0, year: 0, originalString: {}, successfullyParsed: false },
    dateOfExpiry: result.dateOfExpiry && typeof result.dateOfExpiry === "object" ? result.dateOfExpiry : { day: 0, month: 0, year: 0, originalString: {}, successfullyParsed: false },
    sex: result.sex ?? emptyText(),
    address: result.address ?? emptyText(),
    personalIdNumber: result.personalIdNumber ?? emptyText(),
    nationality: result.nationality ?? emptyText(),
    optionalData: result.optionalData ?? emptyText(),
    placeOfBirth: result.placeOfBirth ?? emptyText(),
    issuingStateName: result.issuingStateName ?? emptyText(),
    nationalityCode: result.nationalityCode ?? emptyText(),
    section: result.section ?? emptyText(),
    voterKey: result.voterKey ?? emptyText(),
    bookletNumber: result.bookletNumber ?? emptyText(),
    issueNumber: result.issueNumber ?? emptyText(),
    numeroEmision: result.issueNumber ?? emptyText(), // alias para formularios que usan este uid
    dateOfRegistration:
      result.dateOfRegistration && typeof result.dateOfRegistration === "object"
        ? result.dateOfRegistration
        : { day: 0, month: 0, year: 0, originalString: {}, successfullyParsed: false },
    url: result.url ?? emptyText(),
    secondSurname: result.secondSurname ?? emptyText(),
    mrzString: result.mrzString ?? emptyText(),
    result,
  };

  payload.mrz = safeMrz(result.mrz);
  payload.mrzResult = safeMrz(result.mrzResult || result.mrz);
  payload.mrz.documentNumber = docNum || payload.mrz.documentNumber;
  payload.mrz.sanitizedOpt1 = optData || payload.mrz.sanitizedOpt1;
  if (strFromField(result.mrzString)) payload.mrz.rawMRZString = strFromField(result.mrzString);
  if (payload.mrzResult) {
    payload.mrzResult.documentNumber = docNum || payload.mrzResult.documentNumber;
    payload.mrzResult.sanitizedOpt1 = optData || payload.mrzResult.sanitizedOpt1;
  }

  if (blinkLike.fullDocumentFrontImage) {
    if (imageDataMap?.front) {
      payload.fullDocumentFrontImage = { rawImage: imageDataMap.front, encodedImage: [] };
    } else if (typeof blinkLike.fullDocumentFrontImage === "object" && blinkLike.fullDocumentFrontImage?.rawImage) {
      payload.fullDocumentFrontImage = blinkLike.fullDocumentFrontImage;
    } else {
      payload.fullDocumentFrontImage = { rawImage: null, encodedImage: [] };
    }
  }
  if (blinkLike.fullDocumentBackImage) {
    if (imageDataMap?.back) {
      payload.fullDocumentBackImage = { rawImage: imageDataMap.back, encodedImage: [] };
    } else if (typeof blinkLike.fullDocumentBackImage === "object" && blinkLike.fullDocumentBackImage?.rawImage) {
      payload.fullDocumentBackImage = blinkLike.fullDocumentBackImage;
    } else {
      payload.fullDocumentBackImage = { rawImage: null, encodedImage: [] };
    }
  }
  if (blinkLike.faceImage) {
    if (imageDataMap?.face) {
      payload.faceImage = { rawImage: imageDataMap.face, encodedImage: [] };
    } else if (typeof blinkLike.faceImage === "object" && blinkLike.faceImage?.rawImage) {
      payload.faceImage = blinkLike.faceImage;
    } else {
      payload.faceImage = { rawImage: null, encodedImage: [] };
    }
  }

  return payload;
}

function safeMrz(m) {
  if (!m || typeof m !== "object") return { documentNumber: "", sanitizedOpt1: "", rawMRZString: "" };
  return {
    documentNumber: typeof m.documentNumber === "string" ? m.documentNumber : "",
    sanitizedOpt1: typeof m.sanitizedOpt1 === "string" ? m.sanitizedOpt1 : "",
    rawMRZString: typeof m.rawMRZString === "string" ? m.rawMRZString : "",
    ...m,
  };
}

/**
 * Igual que blinkLikeToBinderPayload pero convierte imágenes base64 a ImageData para que el binder
 * las use en fullDocumentFrontImage.rawImage / fullDocumentBackImage.rawImage / faceImage.rawImage
 * y llame a Jr() -> setFile() correctamente.
 * @param {object} blinkLike
 * @returns {Promise<object>} payload para binder.setOcrResult(payload)
 */
export async function blinkLikeToBinderPayloadAsync(blinkLike) {
  if (!blinkLike) return {};
  const normB64 = (s) => (s && typeof s === "string" ? s.replace(/^data:image\/\w+;base64,/, "").trim() : "");
  const hasB64 = (s) => normB64(s).length > 0;
  const imageDataMap = {};
  if (hasB64(blinkLike.fullDocumentFrontImage)) {
    imageDataMap.front = await base64ToImageData(blinkLike.fullDocumentFrontImage);
  }
  if (hasB64(blinkLike.fullDocumentBackImage)) {
    imageDataMap.back = await base64ToImageData(blinkLike.fullDocumentBackImage);
  }
  if (hasB64(blinkLike.faceImage)) {
    imageDataMap.face = await base64ToImageData(blinkLike.faceImage);
  }
  return blinkLikeToBinderPayloadSync(blinkLike, imageDataMap);
}

function getContainerList(response) {
  const raw = response?.rawResponse ?? response;
  if (!raw) return null;
  const list = raw.ContainerList ?? raw.containerList;
  if (list?.List) return list.List;
  if (list?.list) return list.list;
  if (Array.isArray(list)) return list;
  if (raw.List || raw.list) return raw.List ?? raw.list;
  return null;
}

function getFieldListFromContainer(container, forImages = false) {
  if (!container) return [];
  const byName = forImages
    ? container.Images ?? container.images
    : container.Text ?? container.text;
  if (byName) {
    const list =
      byName.fieldList ?? byName.FieldList ?? byName.field_list ?? byName.List ?? byName.list;
    const arr = Array.isArray(list) ? list : (list?.List ?? list?.list ?? []);
    if (arr.length) return arr;
  }
  const root =
    container.fieldList ?? container.FieldList ?? container.field_list ?? container.List ?? container.list;
  return Array.isArray(root) ? root : (root?.List ?? root?.list ?? []);
}

function getContainerType(container) {
  return (
    container.result_type ??
    container.ResultType ??
    container.resultType ??
    container.ContainerType ??
    container.containerType ??
    container.Type ??
    container.type
  );
}

function findContainerByType(containerList, type) {
  if (!Array.isArray(containerList)) return null;
  const t = Number(type);
  return containerList.find((c) => Number(getContainerType(c)) === t);
}

function getFieldValue(field) {
  let v = field?.value ?? field?.Value ?? field?.ValueBuf;
  if (v != null && v !== "") return String(v).trim();
  const valueList = field?.valueList ?? field?.ValueList;
  if (Array.isArray(valueList) && valueList.length > 0) {
    const first = valueList[0];
    v = first?.value ?? first?.Value;
    if (v != null && v !== "") return String(v).trim();
  }
  return undefined;
}

/** Extrae todos los campos de texto de todos los contenedores (36 Text, 3/61 MRZ, etc.) y devuelve Map(fieldType -> value). */
function extractAllTextFieldsByType(containerList) {
  const byType = new Map();
  if (!Array.isArray(containerList)) return byType;
  for (const item of containerList) {
    const rt = Number(getContainerType(item));
    const hasText = !!(item.Text ?? item.text);
    const isMrz = rt === 3 || rt === 61 || !!(item.MrzPosition ?? item.mrzPosition);
    const isTextContainer = rt === RESULT_TEXT || hasText;
    if (!isTextContainer && !isMrz) continue;
    const fieldList = getFieldListFromContainer(item, false);
    if (!fieldList.length && isMrz) {
      const mrzContainer = item.MrzPosition ?? item.mrzPosition ?? item;
      const mrzStr =
        mrzContainer.MrzString ??
        mrzContainer.mrzString ??
        mrzContainer.MRZ ??
        mrzContainer.mrz;
      if (mrzStr && typeof mrzStr === "string") {
        byType.set(3, mrzStr.trim());
      }
      const fl = item.fieldList ?? item.FieldList ?? [];
      for (const f of fl) {
        const type = f.fieldType ?? f.FieldType ?? f.type;
        const v = getFieldValue(f);
        if (type != null && v) byType.set(Number(type), v);
      }
      continue;
    }
    for (const f of fieldList) {
      const type = f.fieldType ?? f.FieldType ?? f.type;
      const v = getFieldValue(f);
      if (type != null && v) byType.set(Number(type), v);
    }
    const legacy = item.Fields ?? item.fields ?? item.List ?? item.list;
    const legacyArr = Array.isArray(legacy) ? legacy : legacy?.List ?? legacy?.list ?? [];
    for (const f of legacyArr) {
      const type = f?.fieldType ?? f?.FieldType ?? f?.type;
      const v = getFieldValue(f);
      if (type != null && v) byType.set(Number(type), v);
    }
  }
  return byType;
}

function getTextValueByFieldType(containerList, fieldType) {
  const byType = extractAllTextFieldsByType(containerList);
  return byType.get(Number(fieldType));
}

function getImageBase64ByGraphicType(containerList, graphicFieldType) {
  const imagesContainer = findContainerByType(containerList, RESULT_IMAGES);
  const fieldList = getFieldListFromContainer(imagesContainer, true);
  const field = fieldList.find(
    (f) =>
      Number(f?.fieldType ?? f?.FieldType ?? f?.type) === Number(graphicFieldType)
  );
  if (!field) return undefined;
  const valueList = field.valueList ?? field.ValueList ?? [];
  const first = Array.isArray(valueList) && valueList.length > 0 ? valueList[0] : field;
  return first?.value ?? first?.Value ?? field?.value ?? field?.Value;
}

/**
 * Construye un objeto con la forma de resultado BlinkID (recognizer) para
 * usar con binder.setOcrResult(recognizer) sin cambiar el backend.
 *
 * @param {object} regulaResponse - response de Regula (event.detail.data.response)
 * @returns {object} Objeto en formato Blink: { result, fullDocumentImage?, fullDocumentFrontImage?, fullDocumentBackImage?, faceImage?, signatureImage? }
 */
export function regulaToBlinkResult(regulaResponse) {
  const containerList = getContainerList(regulaResponse);
  const defaultResult = getDefaultBlinkResult();
  if (!containerList || !Array.isArray(containerList)) {
    return { result: defaultResult };
  }

  const extracted = {};
  for (const [regulaType, blinkKey] of Object.entries(REGULA_TEXT_FIELD_TO_BLINK)) {
    const value = getTextValueByFieldType(containerList, parseInt(regulaType, 10));
    if (value !== undefined && value !== "") extracted[blinkKey] = value;
  }
  const result = mergeExtractedIntoBlinkResult(defaultResult, extracted);

  const fullDocumentFrontImage = getImageBase64ByGraphicType(
    containerList,
    REGULA_GRAPHIC.DOCUMENT_FRONT
  );
  const fullDocumentBackImage = getImageBase64ByGraphicType(
    containerList,
    REGULA_GRAPHIC.DOCUMENT_REAR
  );
  const faceImage = getImageBase64ByGraphicType(
    containerList,
    REGULA_GRAPHIC.PORTRAIT
  );
  const signatureImage = getImageBase64ByGraphicType(
    containerList,
    REGULA_GRAPHIC.SIGNATURE
  );

  const blinkLike = {
    result,
  };

  if (fullDocumentFrontImage || fullDocumentBackImage) {
    if (fullDocumentFrontImage) blinkLike.fullDocumentFrontImage = fullDocumentFrontImage;
    if (fullDocumentBackImage) blinkLike.fullDocumentBackImage = fullDocumentBackImage;
    blinkLike.fullDocumentImage = fullDocumentFrontImage || fullDocumentBackImage;
  } else if (faceImage) {
    blinkLike.fullDocumentImage = faceImage;
  }

  if (faceImage) blinkLike.faceImage = faceImage;
  if (signatureImage) blinkLike.signatureImage = signatureImage;

  return blinkLike;
}

/**
 * Normaliza la respuesta del backend (/api/verify) al formato Blink (mismas llaves que antes)
 * para no tener que cambiar el backend. Acepta respuesta en formato Regula (ContainerList)
 * o objeto plano con las mismas llaves que Blink.
 *
 * @param {object} backendData - Respuesta del backend, ej. { result, croppedImages?: { front?, back? } }
 * @returns {object} Objeto en formato Blink: { result: {...}, fullDocumentImage?, fullDocumentFrontImage?, fullDocumentBackImage?, faceImage?, signatureImage? }
 */
export function backendResponseToBlink(backendData) {
  if (!backendData) return { result: getDefaultBlinkResult() };

  const rawResult = backendData.result ?? backendData;
  const containerList = getContainerList(rawResult);
  const hasRegulaStructure =
    Array.isArray(containerList) && containerList.length > 0;

  let blinkLike;
  if (hasRegulaStructure) {
    blinkLike = regulaToBlinkResult(rawResult);
  } else {
    blinkLike = { result: normalizeFlatResultToBlink(rawResult) };
    const src = rawResult && typeof rawResult === "object" ? rawResult : {};
    for (const key of BLINK_IMAGE_KEYS) {
      if (src[key] && typeof src[key] === "string") blinkLike[key] = src[key];
    }
    if (!blinkLike.fullDocumentImage && blinkLike.fullDocumentFrontImage) {
      blinkLike.fullDocumentImage = blinkLike.fullDocumentFrontImage;
    }
  }

  return blinkLike;
}

/**
 * Extrae todas las imágenes base64 del contenedor Images (tipo 37) de la respuesta Regula.
 * Orden: típicamente frente, dorso (según pageIndex).
 * @param {object} response - response de Regula
 * @returns {string[]} Array de strings base64
 */
export function getImagesFromResponse(response) {
  const list = getContainerList(response);
  const containers = Array.isArray(list) ? list : list?.List ?? list?.list ?? [];
  const images = [];
  for (const container of containers) {
    const resultType =
      container.result_type ?? container.ResultType ?? container.resultType;
    if (Number(resultType) !== RESULT_IMAGES) continue;
    const fieldList = getFieldListFromContainer(container, true);
    for (const field of fieldList) {
      const valueList = field.valueList ?? field.ValueList ?? [];
      for (const v of valueList) {
        const value = v?.value ?? v?.Value;
        if (value && typeof value === "string") images.push(value);
      }
    }
  }
  return images;
}

/**
 * Extrae imágenes de la respuesta Regula con nombre de campo (para logs).
 * @param {object} response - response de Regula
 * @returns {{ fieldName: string, base64: string }[]}
 */
function getImagesWithNamesFromResponse(response) {
  const list = getContainerList(response);
  const containers = Array.isArray(list) ? list : list?.List ?? list?.list ?? [];
  const out = [];
  for (const container of containers) {
    const resultType =
      container.result_type ?? container.ResultType ?? container.resultType;
    if (Number(resultType) !== RESULT_IMAGES) continue;
    const fieldList = getFieldListFromContainer(container, true);
    for (const field of fieldList) {
      const fieldType = Number(field?.fieldType ?? field?.FieldType ?? field?.type);
      const valueList = field.valueList ?? field.ValueList ?? [];
      const first = valueList?.[0];
      const value = first?.value ?? first?.Value ?? field?.value ?? field?.Value;
      if (value && typeof value === "string") {
        out.push({
          fieldName: GRAPHIC_FIELD_NAMES[fieldType] ?? `Imagen (${fieldType})`,
          base64: value,
        });
      }
    }
  }
  return out;
}

function normBase64(v) {
  if (!v || typeof v !== "string") return "";
  return v.replace(/^data:image\/\w+;base64,/, "").trim();
}

/**
 * Log en consola de los datos OCR (contenedores, campos, MRZ, imágenes), al estilo showOCRResult.
 * Opcionalmente incluye imágenes recortadas del backend (options.croppedImages).
 *
 * @param {object} response - Respuesta Regula (containerList) o backend (con result/List/list)
 * @param {{ croppedImages?: { front?: string, back?: string } }} options
 */
export function logOCRResult(response, options = {}) {
  if (!response) return;
  const list = getContainerList(response)?.List ?? getContainerList(response)?.list ?? getContainerList(response);
  const containerList = Array.isArray(list) ? list : list?.List ?? list?.list ?? [];
  const types = containerList.map(
    (c) => c.result_type ?? c.ResultType ?? c.resultType
  );
  const byType = extractAllTextFieldsByType(containerList);
  const mrz = byType.get(3) ?? null;
  const fields = [];
  for (const [type, value] of byType) {
    if (Number(type) === 3) continue;
    const name = REGULA_TEXT_FIELD_TO_BLINK[Number(type)] ?? `Campo ${type}`;
    fields.push({ name, value: value ?? "" });
  }
  let allImages = getImagesWithNamesFromResponse(response);
  if (options?.croppedImages?.front) {
    allImages = [
      ...allImages,
      { fieldName: "Frente (response.croppedImages)", base64: normBase64(options.croppedImages.front) },
    ];
  }
  if (options?.croppedImages?.back) {
    allImages = [
      ...allImages,
      { fieldName: "Dorso (response.croppedImages)", base64: normBase64(options.croppedImages.back) },
    ];
  }
  const fieldCount = fields.length;
  const hasMrz = !!mrz;
  const imageCount = allImages.length;
  console.log(
    `[Regula] OCR: ${fieldCount} campos, MRZ=${hasMrz}, imágenes: ${imageCount}`,
    fieldCount || hasMrz || imageCount ? "" : "(sin datos)"
  );
  console.log("[Regula] Contenedores recibidos (tipos):", types);
  console.log("[Regula] Datos extraídos (OCR):", { mrz, fields });
  console.log("[Regula] Imágenes:", allImages.map((i) => i.fieldName));
}

export {
  getContainerList,
  getFieldListFromContainer,
  findContainerByType,
  getTextValueByFieldType,
  getImageBase64ByGraphicType,
  REGULA_TEXT_FIELD_TO_BLINK,
  REGULA_GRAPHIC,
  RESULT_TEXT,
  RESULT_IMAGES,
};
